import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { BrgEvent, CalendarDay } from '../types';
import { formatTime } from '../utils';

interface DayViewModalProps {
  day: CalendarDay;
  onClose: () => void;
  onEventClick: (event: BrgEvent) => void;
}

/**
 * Day View Modal Component
 * Displays all events for a selected day
 * Shows event name and time, with click to view details
 * Rendered as a Portal at the document root to avoid positioning context issues
 */
export default function DayViewModal({
  day,
  onClose,
  onEventClick,
}: DayViewModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  // Parse date string directly to avoid timezone issues
  const [year, month, dayStr] = day.fullDate.split('-').map(Number);
  const dayDate = new Date(year, month - 1, dayStr);
  
  const dayName = dayDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const modalContent = (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 99999
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(calc(100% - 32px), 560px)',
          maxHeight: 'auto',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          zIndex: 100000
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#000000',
            color: '#FFFFFF',
            padding: '16px 16px 12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h2 
            style={{
              fontSize: '24px',
              fontWeight: '700',
              margin: '0',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            {dayName} — All Events
          </h2>
          <button
            onClick={onClose}
            style={{
              color: '#FFFFFF',
              fontSize: '24px',
              fontWeight: 'bold',
              lineHeight: 1,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginLeft: '16px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#F0F0F0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#FFFFFF';
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div 
          style={{
            padding: '16px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif'
          }}
        >
          {day.events.length === 0 ? (
            <p 
              style={{
                fontSize: '14px',
                color: '#666666',
                margin: '0'
              }}
            >
              No events scheduled for this day.
            </p>
          ) : (
            <div 
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              {day.events.map(event => (
                <div
                  key={event.id}
                  onClick={() => {
                    onEventClick(event);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: '#FAFAFA',
                    border: '1px solid #EEEEEE',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F5F5F5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FAFAFA';
                  }}
                >
                  {/* Color dot */}
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: event.brgColor,
                      flexShrink: 0
                    }}
                  />

                  {/* Event info */}
                  <div 
                    style={{
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <div 
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#333333'
                      }}
                    >
                      {event.eventName}
                    </div>
                    <div 
                      style={{
                        fontSize: '11px',
                        color: '#888888',
                        marginTop: '3px'
                      }}
                    >
                      {formatTime(event.time)}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div 
                    style={{
                      color: '#CCCCCC',
                      fontSize: '16px',
                      flexShrink: 0
                    }}
                  >
                    ›
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
}
